/**
 * Trigger Store
 *
 * Manages trigger (gatilho) state on the client.
 * Triggers are special campaigns that are pre-cached but only played when
 * the server fires them via SSE event.
 *
 * Supports 3 modes:
 * - RANDOM_ONE: Pick one random content, play once, return to campaigns
 * - ONCE: Play all trigger contents in sequence (1 loop), return to campaigns
 * - CONTINUOUS: Loop trigger contents until server deactivates
 */

import { makeAutoObservable, runInAction } from 'mobx';
import {
  TriggerDefinition,
  TriggerMode,
  PlaylistItem,
} from '~/services/scheduler/types';
import {
  TriggerFiredPayload,
  TriggerDeactivatedPayload,
} from '~/services/sync/types';
import { playbackStore } from './playback.store';

const LOG_PREFIX = '[TriggerStore]';

export type TriggerPlaybackStatus = 'idle' | 'playing';

export interface ActiveTrigger {
  triggerId: number;
  triggerName: string;
  mode: TriggerMode;
  playlist: PlaylistItem[];
  /** For ONCE/RANDOM_ONE: auto-deactivation timer */
  autoDeactivateSeconds?: number;
}

class TriggerStore {
  // Current trigger state
  status: TriggerPlaybackStatus = 'idle';
  activeTrigger: ActiveTrigger | null = null;

  // Trigger definitions from sync (for pre-caching and content lookup)
  triggerDefinitions: Map<string, TriggerDefinition> = new Map();

  // Saved campaign state for restoration after trigger completes
  private savedCampaignPlaylist: PlaylistItem[] | null = null;
  private savedCampaignIndex: number = 0;
  // Snapshot do playbackStore.currentSlotIndex no momento do disparo. Sem
  // isso, applyPlaylistFromPlaybackStore (chamado no próximo sync após o
  // trigger ser desativado) re-aplica a playlist usando um currentSlotIndex
  // stale (ficou no valor anterior ao trigger) — resultado: player volta
  // pro slot 0 (Prada) em vez de retomar no slot que estava tocando.
  private savedSlotIndex: number = 0;

  // Reference to streamStore (set during init).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- evita ciclo de import com StreamStore
  private streamStore: any = null;

  // Reference to schedulerStore (set during init). V088 (2026-08-08) — usado
  // pra enriquecer o payload do trigger com weather/validade/dia/hora do
  // conteúdo antes de aplicar o filtro canônico. Ciclo de import evitado
  // com injeção em stores/index.ts (mesmo padrão do setStreamStore).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private schedulerStore: any = null;

  // Auto-deactivation timer
  private autoDeactivateTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Reentrada pelo relógio do grupo exclusivo — `SyncStore.realignToCanonicalClock`, injetado
   * no `SyncStore.init()` (este arquivo não importa o SyncStore: ele já importa este).
   *
   * <p>Usado só quando há época canônica ({@link playbackStore.canonicalEpoch}). Sem handler
   * injetado o fim de gatilho cai no caminho legado (restaurar o salvo) — best-effort, igual
   * ao `schedulerStore` nulo.</p>
   */
  private realignHandler: ((source: string) => void) | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  // ===========================================================================
  // INITIALIZATION
  // ===========================================================================

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- evita ciclo de import com StreamStore
  setStreamStore(store: any): void {
    this.streamStore = store;
  }

  /**
   * V088 (2026-08-08) — Injeção do SchedulerStore pro enriquecimento do
   * payload do trigger. Chamado pelo composition root (`stores/index.ts`).
   * Se null, o trigger passa direto sem enriquecer (best-effort).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setSchedulerStore(store: any): void {
    this.schedulerStore = store;
  }

  /** Vide {@link realignHandler}. Chamado pelo `SyncStore.init()`. */
  setRealignHandler(handler: ((source: string) => void) | null): void {
    this.realignHandler = handler;
  }

  /**
   * Called by SyncStore when sync returns trigger definitions.
   */
  setTriggerDefinitions(triggers: TriggerDefinition[]): void {
    runInAction(() => {
      this.triggerDefinitions.clear();
      triggers.forEach((trigger) => {
        this.triggerDefinitions.set(trigger.id, trigger);
      });
    });
    console.log(
      `${LOG_PREFIX} 📦 Updated trigger definitions: ${triggers.length} triggers`,
      triggers.map((t) => ({
        id: t.id,
        name: t.name,
        items: t.playlist.length,
      })),
    );
  }

  // ===========================================================================
  // TRIGGER EXECUTION
  // ===========================================================================

  /**
   * Handle SSE trigger_fired event.
   * Interrupts current campaign playback and starts trigger content.
   */
  handleTriggerFired(payload: TriggerFiredPayload): void {
    console.log(`${LOG_PREFIX} 🎯 TRIGGER FIRED:`, {
      triggerId: payload.triggerId,
      triggerName: payload.triggerName,
      mode: payload.mode,
      contentsCount: payload.contents.length,
    });

    // Redisparo durante gatilho ativo: substitui o gatilho, mas o estado da CAMPANHA
    // salvo no primeiro disparo é o único válido — a playlist corrente agora é a do
    // gatilho, e salvá-la de novo faria o retorno "restaurar" o gatilho em vez da
    // campanha. Só o primeiro disparo (vindo de idle) fotografa a campanha.
    const wasIdle = this.status === 'idle';
    if (!wasIdle) {
      console.warn(
        `${LOG_PREFIX} ⚠️ Already playing trigger ${this.activeTrigger?.triggerId}, replacing with ${payload.triggerId}`,
      );
      this.clearAutoDeactivateTimer();
    }

    // Build playlist from SSE payload (authoritative source for durationOverride)
    // Note: we always use buildPlaylistFromPayload instead of triggerDef?.playlist
    // because the SSE payload has the most up-to-date durationOverride values.
    let playlist: PlaylistItem[];

    if (payload.mode === 'RANDOM_ONE') {
      // Pick one random content
      const allContents = this.buildPlaylistFromPayload(payload);
      if (allContents.length === 0) {
        console.log(
          `${LOG_PREFIX} 🚫 trigger ${payload.triggerId} descartado: nenhum conteúdo passa nos filtros (weather/validade/dia/hora)`,
        );
        return;
      }
      const randomIndex = Math.floor(Math.random() * allContents.length);
      playlist = [allContents[randomIndex]];
      console.log(
        `${LOG_PREFIX} 🎲 RANDOM_ONE: picked item ${randomIndex + 1}/${allContents.length}: ${playlist[0].name} (duration: ${playlist[0].duration}s)`,
      );
    } else {
      // ONCE or CONTINUOUS: use all contents in order
      playlist = this.buildPlaylistFromPayload(payload);
      if (playlist.length === 0) {
        console.log(
          `${LOG_PREFIX} 🚫 trigger ${payload.triggerId} descartado: nenhum conteúdo passa nos filtros (weather/validade/dia/hora)`,
        );
        return;
      }
    }

    // Save current campaign state for restoration (só no primeiro disparo — vide wasIdle)
    if (this.streamStore && wasIdle) {
      this.savedCampaignPlaylist = [...this.streamStore.playlist];
      this.savedCampaignIndex = this.streamStore.currentIndex;
      // CRÍTICO: salvar também o slotIndex do playbackStore (fonte da
      // verdade em Tier 2/3). Sem isso, o próximo sync após o trigger
      // re-aplica a playlist a partir de um slotIndex stale → volta
      // pro slot 0. Decisão de produto (Renato 2026-05-27): Opção B —
      // retomar o slot interrompido DESDE O INÍCIO (não preservamos
      // currentTime do vídeo).
      this.savedSlotIndex = playbackStore.currentSlotIndex;
      console.log(
        `${LOG_PREFIX} 💾 Saved campaign state: streamIndex=${this.savedCampaignIndex}, slotIndex=${this.savedSlotIndex}, items=${this.savedCampaignPlaylist.length}`,
      );
    }

    // Set active trigger
    runInAction(() => {
      this.activeTrigger = {
        triggerId: payload.triggerId,
        triggerName: payload.triggerName,
        mode: payload.mode,
        playlist,
        autoDeactivateSeconds: payload.autoDeactivateSeconds,
      };
      this.status = 'playing';
    });

    // Replace stream playlist with trigger content
    if (this.streamStore) {
      this.streamStore.setPlaylist(
        playlist,
        `Trigger: ${payload.triggerName}`,
        true, // isTriggerPlayback flag
      );
      console.log(
        `${LOG_PREFIX} ▶️ Started trigger playback: ${payload.triggerName} (${payload.mode}, ${playlist.length} items)`,
      );
    }

    // Set auto-deactivation for ONCE and RANDOM_ONE
    if (payload.mode !== 'CONTINUOUS') {
      // For RANDOM_ONE: use the PICKED content's duration (not server's MAX)
      // For ONCE: use server's calculated total duration
      const effectiveDuration =
        payload.mode === 'RANDOM_ONE'
          ? playlist[0]?.duration || payload.autoDeactivateSeconds
          : payload.autoDeactivateSeconds;

      if (effectiveDuration) {
        console.log(
          `${LOG_PREFIX} ⏱️ Auto-deactivation: ${effectiveDuration}s (${payload.mode === 'RANDOM_ONE' ? 'picked content duration' : 'server total'})`,
        );
        this.scheduleAutoDeactivation(effectiveDuration);
      }
    }
  }

  /**
   * Handle SSE trigger_deactivated event or auto-completion.
   * Restores campaign playback.
   */
  handleTriggerDeactivated(payload?: TriggerDeactivatedPayload): void {
    const reason = payload?.reason || 'unknown';
    console.log(
      `${LOG_PREFIX} ⏹️ TRIGGER DEACTIVATED: triggerId=${payload?.triggerId || this.activeTrigger?.triggerId}, reason=${reason}`,
    );

    // Guard: if already idle, the client already deactivated (server backup SSE arrived late)
    if (this.status === 'idle') {
      console.log(
        `${LOG_PREFIX} ℹ️ Already idle — ignoring duplicate deactivation (reason=${reason})`,
      );
      this.clearAutoDeactivateTimer();
      return;
    }

    this.clearAutoDeactivateTimer();

    // ── A ÚNICA decisão do retorno: restaurar o salvo × reentrar pelo relógio ──────────
    //
    // Em GRUPO EXCLUSIVO a posição do loop não é um estado: é `(agora − época) mod loop`, e o
    // relógio do grupo continuou andando enquanto este player tocava o gatilho. Restaurar o
    // slot salvo o devolveria ATRASADO em relação aos peers (o pulso de deriva corrigiria
    // segundos depois, com um pulo visível). Então, quando há época canônica, ele reentra
    // onde o grupo ESTÁ — pelo mesmo caminho do impulso `sync_correction`, sem rede e com a
    // sequência atual (os syncs rodaram durante o gatilho; nada obsoleto é restaurado).
    //
    // Sem época (player avulso, grupo normal, UF, playlist normal, fallback) o caminho é o
    // de sempre: slot salvo, desde o início do item (decisão Renato 2026-05-27), com as
    // guardas de 10s contra o sync seguinte zerar o índice.
    //
    // O portão é o MESMO do server: só grupo exclusivo recebe época (R11).
    const anchored =
      playbackStore.canonicalEpoch != null && this.realignHandler != null;
    const savedPlaylist = this.savedCampaignPlaylist;
    const savedIndex = this.savedCampaignIndex;
    const savedSlot = this.savedSlotIndex;

    // Estado do gatilho zera ANTES de reentrar: `applyStreamPlaylist` recusa enquanto
    // `triggerStore.isPlaying`, e o `setPlaylist` precisa ver a playlist corrente como
    // "de gatilho" (isTriggerPlayback) pra fazer o replace completo em vez do idempotente.
    runInAction(() => {
      this.status = 'idle';
      this.activeTrigger = null;
      this.savedCampaignPlaylist = null;
      this.savedCampaignIndex = 0;
      this.savedSlotIndex = 0;
    });

    if (anchored) {
      console.log(
        `${LOG_PREFIX} 🔄 Grupo exclusivo: reentrando pelo relógio canônico (época ${playbackStore.canonicalEpoch})`,
      );
      this.realignHandler!('trigger-deactivated');
      // A reentrada substitui a playlist do gatilho pela do loop. Se NENHUMA ramificação
      // aplicou (sequência vazia, scheduler sem campanha…), o player ficaria preso tocando
      // o gatilho em loop com status idle — pior que voltar atrasado. Rede de segurança:
      // cai no caminho legado abaixo, que sempre devolve a campanha salva.
      if (!this.streamStore?.isTriggerPlayback) {
        console.log(
          `${LOG_PREFIX} ✅ Returned to campaign playback (canonical clock)`,
        );
        return;
      }
      console.warn(
        `${LOG_PREFIX} ⚠️ Reentrada pelo relógio não aplicou playlist — restaurando a campanha salva`,
      );
    }

    // Restore playbackStore.currentSlotIndex ANTES de chamar restorePlaylist.
    // Isso garante que o próximo applyPlaylistFromPlaybackStore (chamado
    // pelo sync seguinte) use o slotIndex correto — sem isso, o player
    // volta pro slot 0 (bug reportado pelo Renato 2026-05-27).
    playbackStore.restoreSlotIndex(savedSlot);
    console.log(
      `${LOG_PREFIX} 🔄 Restored playbackStore.currentSlotIndex=${savedSlot}`,
    );

    // Restore campaign playlist
    if (this.streamStore && savedPlaylist) {
      console.log(
        `${LOG_PREFIX} 🔄 Restoring campaign playlist: ${savedPlaylist.length} items, streamIndex=${savedIndex}`,
      );
      this.streamStore.restorePlaylist(savedPlaylist, 'Campanha', savedIndex);
    }

    console.log(`${LOG_PREFIX} ✅ Returned to campaign playback`);
  }

  /**
   * Called by StreamStore when trigger playlist completes (last item finished).
   * Only relevant for ONCE and RANDOM_ONE modes.
   */
  handleTriggerPlaylistComplete(): void {
    if (this.status !== 'playing' || !this.activeTrigger) return;

    if (this.activeTrigger.mode === 'CONTINUOUS') {
      // CONTINUOUS mode: restart from beginning
      console.log(
        `${LOG_PREFIX} 🔁 CONTINUOUS mode: restarting trigger playlist`,
      );
      if (this.streamStore) {
        this.streamStore.setCurrentIndex(0);
      }
      return;
    }

    // ONCE or RANDOM_ONE: auto-complete
    console.log(
      `${LOG_PREFIX} ✅ Trigger playlist complete (${this.activeTrigger.mode}), returning to campaigns`,
    );
    this.handleTriggerDeactivated({
      triggerId: this.activeTrigger.triggerId,
      reason: 'playlist_complete',
    });
  }

  // ===========================================================================
  // HELPERS
  // ===========================================================================

  get isPlaying(): boolean {
    return this.status === 'playing';
  }

  private buildPlaylistFromPayload(
    payload: TriggerFiredPayload,
  ): PlaylistItem[] {
    // V088 (2026-08-08) — enriquece cada item com metadados do CONTEÚDO
    // (weather + validFrom/until + daysOfWeek + startAt/endAt + schedule)
    // vindos do catálogo local (schedulerStore.campaigns). Sem isso, o
    // payload SSE do trigger só carrega dados mínimos (id/url/duração) e o
    // filterPlaylistBySchedule não teria como aplicar regras do conteúdo.
    // Vide regras-temporais-player.md R2/R4. Best-effort: se não achar no
    // scheduler (conteúdo novo/só via trigger), passa direto sem enriquecer.
    const rawItems = payload.contents
      .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
      .map((content) => {
        const hasDurationOverride =
          typeof content.durationOverride === 'number' &&
          content.durationOverride > 0;
        const enriched =
          this.schedulerStore?.findPlaylistItemByContentId(content.contentId) ??
          null;
        return {
          id: content.contentId,
          type: content.type as 'video' | 'image',
          name: `Trigger ${payload.triggerName}`,
          url: content.url,
          duration: hasDurationOverride
            ? (content.durationOverride as number)
            : content.duration,
          hasDurationOverride,
          fileSize: 0,
          checksum: '',
          mimeType: content.type === 'video' ? 'video/mp4' : 'image/png',
          // Merge dos metadados do CONTEÚDO (weather + validade + dia/hora)
          weatherConditionContent: enriched?.weatherConditionContent ?? null,
          validFrom: enriched?.validFrom ?? undefined,
          validUntil: enriched?.validUntil ?? undefined,
          startAt: enriched?.startAt,
          endAt: enriched?.endAt,
          daysOfWeek: enriched?.daysOfWeek,
          scheduleMode: enriched?.scheduleMode,
          weeklySchedule: enriched?.weeklySchedule,
        };
      });
    // Aplica as 6 camadas AND (mesmo filtro do Tier 4/2/3) — respeita weather
    // + validade + dia/hora do CONTEÚDO. Trigger com playlist vazia após o
    // filtro cai no early-exit do caller (não interrompe a campanha ativa).
    // Best-effort: sem schedulerStore injetado, passa direto (comportamento legado).
    const filtered = this.schedulerStore
      ? this.schedulerStore.filterPlaylistItemsBySchedule(rawItems)
      : rawItems;
    const filteredOut = rawItems.length - filtered.length;
    if (filteredOut > 0) {
      console.log(
        `${LOG_PREFIX} 🚫 ${filteredOut}/${rawItems.length} itens filtrados (weather/validade/dia/hora do conteúdo)`,
      );
    }
    return filtered;
  }

  private scheduleAutoDeactivation(seconds: number): void {
    this.clearAutoDeactivateTimer();
    console.log(`${LOG_PREFIX} ⏱️ Auto-deactivation scheduled in ${seconds}s`);
    this.autoDeactivateTimer = setTimeout(() => {
      console.log(`${LOG_PREFIX} ⏱️ Auto-deactivation timer fired`);
      this.handleTriggerDeactivated({
        triggerId: this.activeTrigger?.triggerId || 0,
        reason: 'auto_timer',
      });
    }, seconds * 1000);
  }

  private clearAutoDeactivateTimer(): void {
    if (this.autoDeactivateTimer) {
      clearTimeout(this.autoDeactivateTimer);
      this.autoDeactivateTimer = null;
    }
  }
}

export const triggerStore = new TriggerStore();
export default TriggerStore;
